import { useNavigate } from 'react-router-dom';

import { Badge, Button, Card, Flex, Spinner, Text } from '@tradener/lumen';

import { COUNTERPARTY_RATING_BG_BY_LABEL, normalizeCounterpartyRating } from '~/pages/OperationLimits/Dashboard/utils/rating';

import useCreditSummary from './useCreditSummary';

function CreditSummary() {
  const navigate = useNavigate();
  const {
    status,
    hasRequiredFields,
    counterpartId,
    counterpartName,
    group,
    competence,
    exposureMtm,
    financialBalance,
    priceReference,
    handleOpenDetails,
  } = useCreditSummary();

  if (status === 'hidden') return null;

  const normalizedGroup = group ? normalizeCounterpartyRating(group) : undefined;

  const onOpenDetails = () => {
    const counterpartPath = encodeURIComponent((counterpartId || '').trim());

    handleOpenDetails();
    navigate(`/limits/dashboard/${counterpartPath}`);
  };

  return (
    <Card
      data-testid="credit-summary"
      display="flex"
      flexDirection="column"
      width="full"
      borderColor="gray.300"
      borderWidth="1px"
      borderRadius="xl"
      bgColor="gray.0"
      p="4"
      rowGap="3"
    >
      <Flex align="center" justify="space-between" gap="3">
        <Text textStyle="body2-semibold">Resumo de crédito</Text>
        <Text textStyle="caption1-regular" color="gray.500">
          Referência de preço: {priceReference}
        </Text>
      </Flex>

      {status === 'loading' && (
        <Flex align="center" gap="2">
          <Spinner size="sm" color="orange.500" />
          <Text textStyle="body2-regular" color="gray.500">
            Atualizando resumo da contraparte...
          </Text>
        </Flex>
      )}

      {status === 'error' && (
        <Text textStyle="body2-regular" color="red.500">
          Não foi possível consultar o resumo de crédito agora. Você ainda pode salvar a operação.
        </Text>
      )}

      {status === 'empty' && hasRequiredFields && (
        <Text textStyle="body2-regular" color="gray.500">
          Nenhum dado de crédito disponível para a contraparte selecionada.
        </Text>
      )}

      {status === 'ready' && (
        <Flex flexDirection="column" gap="2">
          <Flex align="center" justify="space-between" gap="3">
            <Text textStyle="caption1-regular" color="gray.500">
              Contraparte
            </Text>
            <Text textStyle="body2-regular" textAlign="right">
              {counterpartName || '-'}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between" gap="3">
            <Text textStyle="caption1-regular" color="gray.500">
              Classificação
            </Text>
            {normalizedGroup ? (
              <Badge
                data-testid="credit-summary-rating-badge"
                borderRadius="sm"
                px="2"
                py="1"
                bg={COUNTERPARTY_RATING_BG_BY_LABEL[normalizedGroup]}
                color="white"
              >
                {normalizedGroup}
              </Badge>
            ) : (
              <Text textStyle="body2-regular" textAlign="right">
                -
              </Text>
            )}
          </Flex>
          <Flex align="center" justify="space-between" gap="3">
            <Text textStyle="caption1-regular" color="gray.500">
              Prazo de Negociação
            </Text>
            <Text textStyle="body2-regular" textAlign="right">
              {competence || '-'}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between" gap="3">
            <Text textStyle="caption1-regular" color="gray.500">
              Exposição MTM
            </Text>
            <Text textStyle="body2-semibold" textAlign="right">
              {Number(exposureMtm || 0).formatCurrency('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between" gap="3">
            <Text textStyle="caption1-regular" color="gray.500">
              Saldo financeiro
            </Text>
            <Text textStyle="body2-semibold" textAlign="right">
              {Number(financialBalance || 0).formatCurrency('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Text>
          </Flex>
        </Flex>
      )}

      <Flex justify="flex-end">
        <Button
          variant="link"
          color="orange.500"
          onClick={onOpenDetails}
          data-testid="credit-summary-details-button"
          isDisabled={!hasRequiredFields}
          _hover={{ color: 'orange.500' }}
        >
          Ver detalhe completo
        </Button>
      </Flex>
      {!hasRequiredFields && (
        <Text textStyle="caption1-regular" color="gray.500" textAlign="right">
          Selecione contraparte para abrir o detalhe.
        </Text>
      )}
    </Card>
  );
}

export default CreditSummary;
